import { formatDateLong, todayLocalISO } from '@/lib/date-utils'
import { formatExpiryHours } from '@/utils/formatDate'
import type { DateRange, SelectionMode } from '@/types/teacher-request'
import { DisabledFeature } from './DisabledFeature'

interface HeaderBarProps {
  selectedDate: string
  requestConfig: any
  onDateChange: (date: string) => void
  onSubmit: () => void
  onUndo: () => void
  canUndo: boolean
  onBackToCalendar?: () => void
  onProceedToPriorities?: () => void
  selectedLessonsCount?: number

  // Time Picker v3
  selectionMode: SelectionMode
  onSelectionModeChange: (mode: SelectionMode) => void
  dateRange: DateRange | null
  onDateRangeUpdate: (range: DateRange | null) => void  // For datepicker input (no undo)
  onDateRangeCommit: (range: DateRange | null) => void   // For reset/commit (with undo)

  // Request Flow (Phase: priority-v2)
  requestFlow: 'prioritized' | 'automatic'
  onRequestFlowChange: (flow: 'prioritized' | 'automatic') => void
  viewMode?: 'calendar' | 'priorities'
  qualifiedTeachersCount?: number | null
  isSearchingTeachers?: boolean

  // Feature flags for disabled state
  prioritySystemEnabled?: boolean
  featureFlagEligible?: boolean

  // Konzept A — Senden-Sperre: Submit nur mit ≥1 Priorität (Prioritäts-Modus)
  canSubmit?: boolean
  primaryLabel?: string
}

export function HeaderBar({
  selectedDate,
  requestConfig,
  onDateChange,
  onSubmit,
  onUndo,
  canUndo,
  onBackToCalendar,
  onProceedToPriorities,
  selectedLessonsCount,
  selectionMode,
  onSelectionModeChange,
  dateRange,
  onDateRangeUpdate,
  onDateRangeCommit,
  requestFlow,
  onRequestFlowChange,
  viewMode = 'calendar',
  qualifiedTeachersCount = 0,
  isSearchingTeachers = false,
  prioritySystemEnabled = true,
  featureFlagEligible = true,
  canSubmit = true,
  primaryLabel,
}: HeaderBarProps) {
  const minDate = todayLocalISO()
  const isNextStep = requestFlow === 'prioritized' && viewMode === 'calendar'
  const primaryEnabled = isNextStep ? (selectedLessonsCount ?? 0) > 0 : canSubmit
  const resolvedLabel = primaryLabel ?? (isNextStep ? 'Nächster Schritt' : 'Überprüfen & Senden')
  const primaryTitle = primaryEnabled
    ? undefined
    : isNextStep || requestFlow === 'automatic'
      ? 'Mindestens 1 Lektion auswählen'
      : 'Mindestens 1 Priorität setzen (Lehrer durch Anklicken wählen)'
  const handlePrimaryClick = () => {
    if (isNextStep) onProceedToPriorities?.()
    else onSubmit()
  }

  const handleFromChange = (value: string) => {
    if (!value || value < minDate) return
    const newRange = { from: value, to: dateRange?.to || value }
    if (newRange.to < newRange.from) {
      newRange.to = newRange.from
    }
    onDateRangeUpdate(newRange)
  }

  const handleToChange = (value: string) => {
    if (!value || value < minDate || !dateRange?.from) return
    const newRange = { from: dateRange.from, to: value }
    if (newRange.to < newRange.from) {
      newRange.from = newRange.to
    }
    onDateRangeUpdate(newRange)
  }

  const handleResetRange = () => {
    onDateRangeCommit(null)
  }

  // Determine if priority features should be disabled
  const priorityFeaturesDisabled = !prioritySystemEnabled || !featureFlagEligible
  
  // Get disabled reason based on what's disabled
  const getPriorityDisabledReason = () => {
    if (!prioritySystemEnabled) {
      return 'Prioritätssystem ist deaktiviert'
    }
    if (!featureFlagEligible) {
      return 'Prioritätssystem wird schrittweise eingeführt'
    }
    return 'Feature nicht verfügbar'
  }

  const getPriorityDisabledAction = () => {
    if (!prioritySystemEnabled) {
      return 'Kontaktieren Sie den Administrator'
    }
    if (!featureFlagEligible) {
      return 'Ihre Schule ist noch nicht aktiviert'
    }
    return undefined
  }

  return (
    <div className="bg-white border-b px-6 py-4">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Request Flow Toggle */}
          {requestConfig?.allow_automatic_mode ? (
            <DisabledFeature
              enabled={!priorityFeaturesDisabled}
              reason={getPriorityDisabledReason()}
              action={getPriorityDisabledAction()}
              tooltipId="tooltip-request-flow"
            >
              <div className="flex items-center gap-2 bg-indigo-50 rounded-lg p-1 border border-indigo-200">
                <button
                  onClick={() => {
                    if (priorityFeaturesDisabled) return
                    onRequestFlowChange('prioritized')
                  }}
                  onKeyDown={(e) => {
                    if (priorityFeaturesDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                    }
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    requestFlow === 'prioritized'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  } ${priorityFeaturesDisabled ? 'cursor-not-allowed' : ''}`}
                  aria-disabled={priorityFeaturesDisabled}
                >
                  Mit Prioritäten
                </button>
                <button
                  onClick={() => !priorityFeaturesDisabled && onRequestFlowChange('automatic')}
                  onKeyDown={(e) => {
                    if (priorityFeaturesDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                    }
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    requestFlow === 'automatic'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  } ${priorityFeaturesDisabled ? 'cursor-not-allowed' : ''}`}
                  aria-disabled={priorityFeaturesDisabled}
                >
                  Automatisch
                </button>
              </div>
            </DisabledFeature>
          ) : (
            <span className="px-3 py-1.5 bg-indigo-50 rounded-lg text-sm font-medium text-indigo-600 border border-indigo-200">
              Mit Prioritäten
            </span>
          )}

          {/* Mode Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onSelectionModeChange('single')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectionMode === 'single'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Einzelner Tag
            </button>
            <button
              onClick={() => onSelectionModeChange('range')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectionMode === 'range'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Zeitraum
            </button>
          </div>

          {/* Date Inputs */}
          {selectionMode === 'single' ? (
            <input
              type="date"
              value={selectedDate}
              min={minDate}
              onChange={(e) => {
                const v = e.target.value
                if (v && v < minDate) return
                onDateChange(v)
              }}
              className="px-3 py-2 border rounded-lg"
              aria-label="Datum auswählen"
            />
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange?.from || ''}
                min={minDate}
                onChange={(e) => handleFromChange(e.target.value)}
                className="px-3 py-2 border rounded-lg"
                aria-label="Von-Datum"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={dateRange?.to || ''}
                min={minDate}
                onChange={(e) => handleToChange(e.target.value)}
                className="px-3 py-2 border rounded-lg"
                aria-label="Bis-Datum"
              />
              {dateRange && (
                <button
                  onClick={handleResetRange}
                  className="px-2 py-2 text-gray-500 hover:text-red-500 transition-colors"
                  aria-label="Zeitraum zurücksetzen"
                  title="Zeitraum zurücksetzen"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* Selected lessons count */}
          {selectedLessonsCount !== undefined && selectedLessonsCount > 0 && (
            <span className="text-sm text-blue-600 font-medium">
              {selectedLessonsCount} Lektion{selectedLessonsCount !== 1 ? 'en' : ''} ausgewählt
            </span>
          )}

          {/* Config info badge */}
          {requestConfig && (
            <span
              className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded cursor-help"
              title={`Max. ${requestConfig.max_days_per_request} Tage, ${requestConfig.max_lessons_per_request} Lektionen, Ablaufzeit: ${formatExpiryHours(requestConfig.default_expiry_hours)}, Offene Anfragen: ${requestConfig.current_pending_requests ?? '?'} / ${requestConfig.max_pending_requests_per_teacher}`}
            >
              Max {requestConfig.max_days_per_request}T / {formatExpiryHours(requestConfig.default_expiry_hours)}
            </span>
          )}

          {/* Qualified teachers count for automatic mode */}
          {requestFlow === 'automatic' && selectedLessonsCount !== undefined && selectedLessonsCount > 0 && (isSearchingTeachers || qualifiedTeachersCount != null) && (
            <span className="text-sm text-green-600 font-medium">
              {isSearchingTeachers ? (
                <span className="flex items-center gap-1">
                  <span className="animate-pulse">Suche Lehrer...</span>
                </span>
              ) : (
                `${qualifiedTeachersCount} qualifizierte Lehrer`
              )}
            </span>
          )}

          {/* Back to calendar */}
          {onBackToCalendar && (
            <button
              onClick={onBackToCalendar}
              className="px-3 py-2 border rounded-lg hover:bg-gray-50"
            >
              ← Zurück zum Kalender
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canUndo && (
            <button
              onClick={onUndo}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Rückgängig
            </button>
          )}
          <button
            type="button"
            onClick={handlePrimaryClick}
            aria-disabled={!primaryEnabled}
            title={primaryTitle}
            className={`px-4 py-2 rounded-lg transition-colors ${
              primaryEnabled
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {resolvedLabel}
          </button>
        </div>
      </div>

      {/* Range summary badge */}
      {selectionMode === 'range' && dateRange?.from && dateRange?.to && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-sm text-gray-600">
            Zeitraum: {formatDateLong(dateRange.from)} – {formatDateLong(dateRange.to)}
          </span>
        </div>
      )}
    </div>
  )
}
